// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useEffect, useState } from 'react';

import { Button, Text, getDefaultZIndex } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNil } from 'helpers/isNotNil';
import { useLocalStorage } from 'hooks/useLocalStorage';

import { CloudSpoolingHelpLink } from '../constants';
import { useAutodeskAuth } from './useAutodeskAuth';

// This modal must be able to appear above ALL other modals.
const AUTODESK_AUTH_WRAPPER_Z_INDEX = getDefaultZIndex('modal') + 5;

type Props = {
  /** Allows the AutodeskAuthWrapped to wait to ask for Auth (if false). */
  authRequired?: boolean;
  children: ReactNode;
  fullWidth?: boolean;
  onAuthRejected?: () => void;
};

/** `children` of this component will only be rendered the user has successfully authenticated with Autodesk. */
export const AutodeskAuthWrapper = ({ authRequired = true, children, onAuthRejected, fullWidth }: Props) => {
  const [authRejected, setAuthRejected] = useLocalStorage('AUTODESK_AUTH_REJECTED', false);
  const { autodeskAuth, loading, redirectToAutodeskLogin } = useAutodeskAuth();
  const [authNeeded, setAuthNeeded] = useState(false);

  useEffect(() => {
    if (!authRejected && authRequired) {
      setAuthNeeded(isNil(autodeskAuth));
    }
  }, [authRejected, authRequired, autodeskAuth]);

  useEffect(() => {
    if (authRejected) onAuthRejected?.();
  }, [authRejected, onAuthRejected]);

  return (
    <>
      <WrappedModal
        zIndex={AUTODESK_AUTH_WRAPPER_Z_INDEX}
        title="Confirm redirect"
        opened={authNeeded}
        loading={loading}
        onConfirm={() => {
          void redirectToAutodeskLogin({
            state: { previousLoc: `${window.location.pathname}${window.location.search}` },
          });
        }}
        closeOnConfirm={false}
        onClose={() => {
          setAuthNeeded(false);
          setAuthRejected(true);
        }}
        confirmationText="Take me there"
      >
        <Text fz="sm">
          In order for EVOLVE to connect to your Autodesk projects, you will need to authenticate with Autodesk. You
          will be redirected to the Autodesk login page. Would you like to proceed?
        </Text>
        <Text fz="sm" c="dimmed" mt="xs">
          Note: This integration requires the{' '}
          <a className="link" href={CloudSpoolingHelpLink} target="_blank" rel="noreferrer">
            EVOLVE Shop
          </a>{' '}
          Autodesk integration to be installed.
        </Text>
      </WrappedModal>
      {isNil(autodeskAuth) ? (
        <AgGridStyleTooltip label="You must authenticate with Autodesk to use this feature.">
          <Button
            onClick={() => setAuthNeeded(true)}
            variant="outline"
            fullWidth={fullWidth}
            maw={fullWidth ? undefined : 380}
            leftSection={<EvolveIcon icon="AccessLock" />}
          >
            Authenticate with Autodesk
          </Button>
        </AgGridStyleTooltip>
      ) : (
        children
      )}
    </>
  );
};
